// frontend/src/pages/dashboard/Dashboard.jsx

import { useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion, useReducedMotion } from 'framer-motion';
import useAuth from '../../hooks/useAuth';
import backgroundImage from '../../assets/background.png';
import './Dashboard.css';

/* ---------- Ícones ---------- */
const iconProps = {
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 2,
  strokeLinecap: 'round',
  strokeLinejoin: 'round',
  'aria-hidden': true,
};

const TrophyIcon = () => (
  <svg {...iconProps}>
    <path d="M8 21h8M12 17v4M7 4h10v5a5 5 0 0 1-10 0V4Z" />
    <path d="M17 5h3v2a3 3 0 0 1-3 3M7 5H4v2a3 3 0 0 0 3 3" />
  </svg>
);
const ShieldIcon = () => (
  <svg {...iconProps}>
    <path d="M12 3 5 6v6c0 4.5 3 7.5 7 9 4-1.5 7-4.5 7-9V6l-7-3Z" />
  </svg>
);
const UsersIcon = () => (
  <svg {...iconProps}>
    <circle cx="9" cy="8" r="3.5" />
    <path d="M2.5 20c0-3.5 3-6 6.5-6s6.5 2.5 6.5 6" />
    <path d="M16 4.5a3.5 3.5 0 0 1 0 7M18 14.3c2 .7 3.5 2.6 3.5 5.7" />
  </svg>
);
const SwapIcon = () => (
  <svg {...iconProps}>
    <path d="M4 8h14l-3-3M20 16H6l3 3" />
  </svg>
);
const ListIcon = () => (
  <svg {...iconProps}>
    <path d="M8 6h12M8 12h12M8 18h12M4 6h.01M4 12h.01M4 18h.01" />
  </svg>
);
const PlusIcon = () => (
  <svg {...iconProps}>
    <path d="M12 5v14M5 12h14" />
  </svg>
);

/* ---------- Seções do painel ---------- */
const sections = [
  {
    title: 'Campeonatos',
    description: 'Crie competições e acompanhe as que você gerencia.',
    icon: <TrophyIcon />,
    buttons: [
      { label: 'Ver campeonatos', to: '/championships', icon: <ListIcon /> },
      { label: 'Novo campeonato', to: '/championships/new', icon: <PlusIcon />, primary: true },
    ],
  },
  {
    title: 'Times',
    description: 'Cadastre e gerencie as equipes.',
    icon: <ShieldIcon />,
    buttons: [
      { label: 'Ver times', to: '/teams', icon: <ListIcon /> },
      { label: 'Novo time', to: '/teams/new', icon: <PlusIcon />, primary: true },
    ],
  },
  {
    title: 'Jogadores',
    description: 'Cadastre e gerencie os atletas.',
    icon: <UsersIcon />,
    buttons: [
      { label: 'Ver jogadores', to: '/players', icon: <ListIcon /> },
      { label: 'Novo jogador', to: '/players/new', icon: <PlusIcon />, primary: true },
    ],
  },
  {
    title: 'Mercado da Bola',
    description: 'Inscreva jogadores e veja quem está no mercado.',
    icon: <SwapIcon />,
    buttons: [
      { label: 'Ver jogadores', to: '/market', icon: <ListIcon /> },
      { label: 'Fazer inscrição', to: '/market/create', icon: <PlusIcon />, primary: true },
    ],
  },
];

const isAllowedRole = (user) => {
  const role = user?.role?.toLowerCase();
  return role === 'admin' || role === 'gestor';
};

const Dashboard = () => {
  const navigate = useNavigate();
  const { user, isLoading: authLoading } = useAuth();
  const reduceMotion = useReducedMotion();

  useEffect(() => {
    if (authLoading) return;

    if (user) {
      if (!isAllowedRole(user)) {
        navigate('/championships', { replace: true });
      }
    } else {
      navigate('/login', { replace: true });
    }
  }, [user, authLoading, navigate]);

  if (authLoading || !isAllowedRole(user)) {
    return <div className="dashboard-loading-screen">Carregando...</div>;
  }

  const firstName = user.name?.split(' ')[0];

  // Uma única entrada orquestrada: os cards aparecem em sequência ao abrir a página
  const container = {
    hidden: {},
    show: { transition: { staggerChildren: reduceMotion ? 0 : 0.08 } },
  };
  const item = {
    hidden: reduceMotion ? { opacity: 1 } : { opacity: 0, y: 16 },
    show: { opacity: 1, y: 0, transition: { duration: 0.4, ease: 'easeOut' } },
  };

  return (
    <div className="dashboard-master">
      <div
        className="dashboard-background"
        style={{ backgroundImage: `url(${backgroundImage})` }}
        aria-hidden="true"
      />

      <div className="dashboard-content">
        <header className="dashboard-header">
          <h1>{firstName ? `Olá, ${firstName}` : 'Painel'}</h1>
          <p>O que você quer gerenciar hoje?</p>
        </header>

        <motion.div
          className="sections-wrapper"
          variants={container}
          initial="hidden"
          animate="show"
        >
          {sections.map((section) => (
            <motion.section key={section.title} className="dashboard-section" variants={item}>
              <div className="section-head">
                <div className="section-icon">{section.icon}</div>
                <div>
                  <h2 className="section-title">{section.title}</h2>
                  <p className="section-description">{section.description}</p>
                </div>
              </div>

              <div className="button-group">
                {section.buttons.map((btn) => (
                  <Link
                    key={btn.to}
                    to={btn.to}
                    className={`action-button${btn.primary ? ' primary' : ''}`}
                  >
                    {btn.icon}
                    {btn.label}
                  </Link>
                ))}
              </div>
            </motion.section>
          ))}
        </motion.div>
      </div>
    </div>
  );
};

export default Dashboard;
